import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import EmployeeCornerSidebar from "./EmployeeCornerSidebar";

// Helper to format date for display
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return "-";
  const [y, m, d] = dateStr.split("-");
  return `${d}-${m}-${y}`; // Returns DD-MM-YYYY
};
const formatOTDisplay = (otValue) => {
  const val = parseFloat(otValue);
  if (!val || val <= 0) return "--";
  const totalMinutes = Math.round(val * 60);
  if (totalMinutes < 240) return "--";
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}h ${m}m`;
};

const formatWeekday = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { weekday: "short" });
};

const isNonWorkingStatus = (status = "") =>
  status === "OFF" ||
  status === "Absent" ||
  status.includes("SL") ||
  status.includes("CL") ||
  status.includes("(OFF)");

const getDisplayValue = (record, field) => {
  if (isNonWorkingStatus(record.status)) return "--";
  const value = record[field];
  if (!value || value === "" || value === "NaNh NaNm") return "--";
  return value;
};

const buildFullName = (...parts) =>
  parts
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();


const EmployeeAttendance = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [employeeFullName, setEmployeeFullName] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [loggedUser, setLoggedUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("employeeUser")) || null;
    } catch (error) {
      return null;
    }
  });

  const confirmEarlyPunchOutToast = (message) =>
    new Promise((resolve) => {
      toast.custom(
        (t) => (
          <div
            className={`w-[92vw] max-w-md rounded-xl border border-orange-200 bg-white p-4 shadow-xl transition-all ${
              t.visible ? "animate-enter" : "animate-leave"
            }`}
          >
            <p className="text-sm font-semibold text-gray-800">{message}</p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-md border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700"
                onClick={() => {
                  toast.dismiss(t.id);
                  resolve(false);
                }}
              >
                No
              </button>
              <button
                type="button"
                className="rounded-md bg-orange-600 px-3 py-1 text-sm font-semibold text-white"
                onClick={() => {
                  toast.dismiss(t.id);
                  resolve(true);
                }}
              >
                Yes
              </button>
            </div>
          </div>
        ),
        { duration: Infinity }
      );
    });

  const fetchHistory = async () => {
    if (!loggedUser?.employeeUserId) return;
    try {
      const res = await axios.get(`/api/attendance/my/${loggedUser.employeeUserId}`);
      setHistory(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const syncLoggedUser = () => {
      try {
        const storedUser = JSON.parse(localStorage.getItem("employeeUser")) || null;
        if (storedUser?.employeeUserId) {
          setLoggedUser(storedUser);
        }
      } catch (error) {
        console.error("Could not read employee login from localStorage");
      }
    };

    syncLoggedUser();

    const intervalId = window.setInterval(() => {
      const storedUser = JSON.parse(localStorage.getItem("employeeUser")) || null;
      if (storedUser?.employeeUserId) {
        setLoggedUser(storedUser);
        window.clearInterval(intervalId);
      }
    }, 500);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const getMasterData = async () => {
      try {
        const res = await axios.get("/api/employees");
        if (res.data && Array.isArray(res.data)) {
          const targetEmployee = res.data.find(emp => emp.employeeUserId === loggedUser.employeeUserId);
          if (targetEmployee) {
            const fName = targetEmployee.firstName || "";
            const mName = targetEmployee.middleName || "";
            const lName = targetEmployee.lastName || "";
            const fullName = `${fName} ${mName} ${lName}`.replace(/\s+/g, ' ').trim();
            setEmployeeFullName(fullName);
          }
        }
      } catch (err) {
        console.error("Could not access the employee list API");
      }
    };

    if (loggedUser?.employeeUserId) {
      getMasterData();
      fetchHistory();
    }
  }, [loggedUser?.employeeUserId]);

  useEffect(() => {
    if (!loggedUser?.employeeUserId) return undefined;

    const intervalId = window.setInterval(() => {
      fetchHistory();
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, [loggedUser?.employeeUserId]);

// --- NEW LOGIC FOR NIGHT SHIFT ---
  const todayStr = new Date().toISOString().split('T')[0];
  
  // Get Yesterday's date string
  const yesterdayDate = new Date();
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const yesterdayStr = yesterdayDate.toISOString().split('T')[0];
  
  const allRecords = history.flatMap((doc) => doc.records || []);
  
  // Find records for both days
  const todaysRecord = allRecords.find(rec => rec.date === todayStr);
  const yesterdaysRecord = allRecords.find(rec => rec.date === yesterdayStr);

  // Check if Yesterday's night shift is still waiting for a Punch Out
  const isNightShiftPending = yesterdaysRecord && 
                             yesterdaysRecord.checkInTime && 
                             (!yesterdaysRecord.checkOutTime || yesterdaysRecord.checkOutTime === "--" || yesterdaysRecord.checkOutTime === "");

  const isTodayFinalizedWithoutPunch =
    !!todaysRecord &&
    (
      ["Absent", "OFF"].includes(todaysRecord.status || "") ||
      (todaysRecord.status || "").includes("SL") ||
      (todaysRecord.status || "").includes("CL")
    );

  // Update button status
  // If night shift is pending, we use yesterday's status. Otherwise, use today's.
  const hasIn = isNightShiftPending || (todaysRecord && !!todaysRecord.checkInTime);
  const hasOut = isNightShiftPending ? false : (todaysRecord && !!todaysRecord.checkOutTime && todaysRecord.checkOutTime !== "--");
  const isActionMarked = isTodayFinalizedWithoutPunch || (hasIn && hasOut);
  // ---------------------------------

  const getLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject("Geolocation not supported");
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
      },
      () => reject("Location permission denied"),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  });
};

  const handleAttendanceAction = async () => {
    if (!loggedUser || !loggedUser.employeeUserId) {
      toast.error("You must be logged in!");
      return;
    }

  setLoading(true);
  try {
    const storageName = buildFullName(
      loggedUser.firstName,
      loggedUser.middleName,
      loggedUser.lastName
    );
    const nameToStore = employeeFullName || storageName || loggedUser.employeeUserId;
    const loc = await getLocation();
    const submitAttendance = async (confirmEarlyPunchOut = false) =>
      axios.post("/api/attendance/mark", {
      employeeId: loggedUser.employeeID,
      employeeUserId: loggedUser.employeeUserId,
      employeeName: nameToStore,
      latitude: loc.latitude,
      longitude: loc.longitude,
      accuracy: loc.accuracy,
      confirmEarlyPunchOut,
    });

    const res = await submitAttendance(false);
    
    // Check if the backend returned a successful save but with an "Absent" status
    if (res.data.status === "Absent") {
      toast.error(res.data.message, { duration: 5000 }); // Red toast for late entry
    } else {
      toast.success(res.data.message); // Green toast for "Present" or "Out"
    }

    fetchHistory();
  } catch (err) {
    if (err.response?.status === 409 && err.response?.data?.requiresConfirmation) {
      const shouldPunchOut = await confirmEarlyPunchOutToast(
        err.response.data.message || "Your shift has not ended yet. Do you still want to punch out?"
      );

      if (shouldPunchOut) {
        try {
          const storageName = buildFullName(
            loggedUser.firstName,
            loggedUser.middleName,
            loggedUser.lastName
          );
          const nameToStore = employeeFullName || storageName || loggedUser.employeeUserId;
          const loc = await getLocation();
          const confirmRes = await axios.post("/api/attendance/mark", {
            employeeId: loggedUser.employeeID,
            employeeUserId: loggedUser.employeeUserId,
            employeeName: nameToStore,
            latitude: loc.latitude,
            longitude: loc.longitude,
            accuracy: loc.accuracy,
            confirmEarlyPunchOut: true,
          });
          toast.success(confirmRes.data.message);
          fetchHistory();
        } catch (confirmErr) {
          toast.error(confirmErr.response?.data?.message || "Error marking attendance");
        }
      }
      return;
    }

    // This catches the 400 error (Too Early / No Shift) and shows the backend message
    const errorMsg = err.response?.data?.message || "Error marking attendance";
    toast.error(errorMsg);
  } finally {
    setLoading(false);
  }
};

  const filteredRecords = allRecords
    .filter((rec) => rec.date && rec.date.startsWith(selectedMonth))
    .sort((a, b) => String(a.date).localeCompare(String(b.date)));

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
      <EmployeeCornerSidebar />
      <div className="flex-1 p-4 pb-24 sm:p-6 sm:pb-6">
        
      {/* Top Header Section */}
      <div className="bg-white rounded-xl shadow-md p-4 mb-6 border-t-4 border-dorika-blue sticky top-0 z-20">
        {/* Changed flex-col to a grid on mobile, and flex-row on larger screens */}
        <div className="grid grid-cols-2 md:flex md:flex-row items-center justify-between gap-4">
          
          {/* 1. Title - Stays left */}
          <h2 className="text-lg md:text-xl font-bold text-gray-800 whitespace-nowrap order-1">
            Daily Attendance
          </h2>

          {/* 2. Today's Date - Moves to top right on mobile */}
          <div className="text-blue-600 font-semibold whitespace-nowrap text-sm md:text-base text-right order-2 md:order-4">
            {formatDateDisplay(todayStr)}
          </div>

          {/* 3. Filter - Bottom left on mobile */}
          <div className="flex items-center gap-2 order-3 md:order-2">
            <span className="text-xs md:text-sm font-medium text-gray-500">Filter:</span>
            <input 
              type="month" 
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="border rounded-md p-1 text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 w-full max-w-[120px]"
            />
          </div>

          {/* 4. Action Button - Bottom right on mobile */}
          <div className="flex justify-end order-4 md:order-3">
            <button
              onClick={handleAttendanceAction}
              disabled={loading || isActionMarked}
              className={`${
                isActionMarked ? "bg-gray-500" : hasIn ? "bg-orange-600 hover:bg-orange-700" : "bg-green-600 hover:bg-green-700"
              } text-white px-4 md:px-8 py-1 rounded-lg font-bold transition-all shadow-md disabled:bg-gray-400 whitespace-nowrap text-sm md:text-base w-full md:w-auto`}
            >
              {loading 
                ? "..." 
                : isNightShiftPending 
                  ? "OUT" 
                  : isTodayFinalizedWithoutPunch
                    ? "MARKED"
                  : (hasIn && !hasOut) 
                    ? "OUT" 
                    : (hasIn && hasOut) 
                      ? "MARKED" 
                      : "IN"}
            </button>
          </div>
        </div>
      </div>

        {/* History Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="bg-dorika-blue p-3 text-white font-bold text-center">
            Attendance History ({selectedMonth})
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-center">
        <thead className="bg-gray-100 text-gray-700">
          <tr>
            <th className="p-3 border">Date</th>
            <th className="p-3 border">Shift</th>
            <th className="p-3 border text-green-600">Punch In</th> {/* New */}
            <th className="p-3 border">Shift Start</th> {/* New */}
            <th className="p-3 border">Shift End</th> {/* New */}
            <th className="p-3 border text-orange-600">Punch Out</th> {/* New */}
            <th className="p-3 border text-blue-600">Shift Work Time</th> {/* Official */}
            <th className="p-3 border text-purple-600">Actual Work Time</th>
            <th className="p-3 border text-red-600">OT Hours</th>
            <th className="p-3 border">Status</th>
          </tr>
        </thead>
          <tbody>
                {filteredRecords.length > 0 ? (
                  filteredRecords.slice().reverse().map((rec, index) => {
                   

                    return (
                      <tr 
                        key={index} 
                        className={`border-b ${rec.status.includes('Holiday') ? 'bg-yellow-50' : 'hover:bg-blue-50'}`}
                      >
                        <td className="p-3 border font-medium">
                          <div className="flex flex-col items-center">
                            <span>{formatDateDisplay(rec.date)}</span>
                            <span className="text-[11px] font-semibold uppercase text-gray-500">
                              {formatWeekday(rec.date)}
                            </span>
                          </div>
                        </td>
                        <td className="p-3 border font-bold text-indigo-600">{rec.shiftCode || "--"}</td>
                        <td className="p-3 border font-semibold text-green-700">{getDisplayValue(rec, "checkInTime")}</td>
                        <td className="p-3 border text-gray-500 font-semibold italic">{rec.shiftStartTime || "--"}</td>
                        <td className="p-3 border text-gray-500 font-semibold italic">{rec.shiftEndTime || "--"}</td>
                        <td className="p-3 border font-semibold text-orange-700">{getDisplayValue(rec, "checkOutTime")}</td>
                        
                        {/* 7. Shift Work Duration (Rounded) */}
                        <td className="p-3 border font-bold text-blue-700">
                          {isNonWorkingStatus(rec.status) ? "--" : (rec.workDuration || "--")}
                        </td>

                        {/* 8. Actual Work Duration (Raw Punch Time from DB) */}
                        <td className="p-3 border font-bold text-purple-700">
                          {getDisplayValue(rec, "actualWorkDuration")}
                        </td>
                        <td className="p-3 border">
                        {rec.isOT && rec.otHours > 0 && !isNonWorkingStatus(rec.status) ? (
                      <span className="bg-red-100 text-red-600 px-2 py-1 rounded-md font-bold">
                        {formatOTDisplay(rec.otHours)}
                      </span>
                          ) : (
                            <span className="text-gray-400">--</span>
                          )}
                        </td>
                       <td className="p-3 border">
                        <div className="flex flex-col items-center justify-center gap-1">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                            rec.status === 'Present' ? 'bg-green-100 text-green-700' :
                            rec.status === 'Absent'  ? 'bg-red-100 text-red-700' :
                            rec.status === 'OFF'     ? 'bg-gray-100 text-gray-700' :
                            rec.status.includes('SL') ? 'bg-orange-100 text-orange-700' :
                            rec.status.includes('CL') ? 'bg-blue-100 text-blue-700' :
                            'bg-yellow-100 text-yellow-700' // Default for Holidays or others
                          }`}>
                            {rec.status}
                          </span>
                          
                          {/* Late Entry Indicator */}
                          {(rec.isLateEntry || (rec.status === 'Present' && rec.isLate)) && (
                            <span className="text-[10px] text-red-500 font-bold uppercase animate-pulse">
                              Late Entry
                            </span>
                          )}
                        </div>
                      </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="10" className="p-10 text-gray-400 italic text-center">No records found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeAttendance;
